<div class="tw-w-full tw-flex tw-flex-col">
    <!-- First div: Search, Tabs, and Header Actions -->
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-4">
        <!-- Left Side: Search Bar -->
        <div
            class="tw-flex tw-flex-row tw-gap-4 tw-items-center tw-justify-between">
            <mat-form-field
                appearance="fill"
                class="filter-search-field tw-flex-shrink-0 tw-mt-5">
                <mat-label>{{
                    _keysForFilteringObj.length === 1
                        ? (_keysForFilteringObj[0].label | translate)
                        : ('OUTGOING-GOODS.SEARCH' | translate)
                }}</mat-label>
                @if (!searchText()) {
                    <mat-icon
                        matPrefix
                        fontSet="material-symbols-outlined"
                        >search</mat-icon
                    >
                }
                @if (searchText()) {
                    <mat-icon
                        class="tw-cursor-pointer"
                        matPrefix
                        (click)="resetFilter()"
                        fontSet="material-symbols-outlined"
                        >delete</mat-icon
                    >
                }
                <input
                    matInput
                    #filterSearch
                    [value]="getDisplayValue()"
                    (input)="onSearchTextChange($event)"
                    [readonly]="
                        loading() ||
                        isStatus ||
                        isDiscrepancies ||
                        isPalletTickets
                    "
                    autocomplete="off" />
                <mat-icon
                    aria-label="expand-filter"
                    matSuffix
                    [matMenuTriggerFor]="filterMenu"
                    fontSet="material-symbols-outlined"
                    >expand_more</mat-icon
                >
            </mat-form-field>

            <!-- Center: Tab Buttons -->
            @if (shouldShowViewToggle()) {
                <div class="tw-flex tw-items-center">
                    <mat-button-toggle-group
                        [value]="currentViewType()"
                        (change)="onViewTypeChange($event)">
                        @for (view of availableViews(); track view) {
                            <mat-button-toggle
                                [value]="view.type"
                                [disabled]="!view.enabled"
                                [aria-label]="view.name + '-button'"
                                class="tw-flex tw-items-center">
                                <span>{{
                                    view.translationKey | translate
                                }}</span>
                                @if (
                                    view.type === 'notifications' &&
                                    hasNewNotifications
                                ) {
                                    <mat-icon
                                        color="primary"
                                        fontSet="material-symbols-outlined"
                                        class="bell-shake-animation tw-ml-2">
                                        notifications_active
                                    </mat-icon>
                                }
                            </mat-button-toggle>
                        }
                    </mat-button-toggle-group>
                </div>
            }
        </div>

        <!-- Right Side: Header Actions -->
        @if (headerActions && headerActions.length > 0) {
            <div
                class="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 tw-mt-5">
                @for (action of headerActions; track action) {
                    <button
                        mat-raised-button
                        [color]="action.color || 'primary'"
                        [disabled]="isHeaderActionDisabled(action.id)"
                        [style.display]="
                            isHeaderActionVisible(action.id)
                                ? 'inline-block'
                                : 'none'
                        "
                        [matTooltip]="
                            action.tooltip ? (action.tooltip | translate) : ''
                        "
                        [class]="
                            (action.cssClasses || 'tw-h-12 xl:!tw-text-base') +
                            ' tw-flex tw-items-center tw-justify-center'
                        "
                        [attr.aria-label]="action.id + '-button'"
                        (click)="onHeaderActionClick(action.id)">
                        <!-- Icon -->
                        @if (action.icon) {
                            <mat-icon
                                fontSet="material-symbols-outlined"
                                matPrefix>
                                {{ action.icon }}
                            </mat-icon>
                        }
                        @if (action.svgIcon) {
                            <mat-icon
                                matPrefix
                                [svgIcon]="action.svgIcon">
                            </mat-icon>
                        }
                        <!-- Label -->
                        <span>{{ action.translationKey | translate }}</span>
                    </button>
                }
            </div>
        }
    </div>

    <!-- Second div: Show Closed Toggle, Location Filter, and Column Filter -->
    <div
        class="tw-flex tw-flex-row tw-gap-4 tw-items-center tw-justify-between">
        <!-- Show Closed Toggle -->
        <mat-checkbox
            aria-label="show-closed-label"
            [checked]="showClosed()"
            (change)="onShowClosedChange($event.checked)"
            [disabled]="loading()"
            class="show-closed-checkbox">
            @if (showClosedLabel) {
                <span>{{ showClosedLabel | translate }}</span>
            }
            @if (!showClosedLabel) {
                <span>
                    {{ 'OUTGOING-GOODS.SHOW-CLOSED' | translate }}
                </span>
            }
        </mat-checkbox>

        <!-- Location Filter Button -->
        <div class="tw-flex tw-flex-row tw-gap-4 tw-items-center">
            @if (shouldShowLocationFilter) {
                <button
                    mat-button
                    class="tw-mr-1"
                    [ngClass]="
                        isFilteringLocations()
                            ? 'tw-text-red-400'
                            : 'tw-text-black'
                    "
                    (click)="onLocationFilterClick()"
                    [disabled]="loading()">
                    {{ 'GENERAL.FILTER-SITES' | translate }}
                    @if (getFilteredLocationsCount() > 0) {
                        <span>({{ getFilteredLocationsCount() }})</span>
                    }
                </button>
            }

            <!-- Column Display Button -->
            @if (shouldShowColumnFilter) {
                <button
                    mat-button
                    (click)="onColumnFilterClick()"
                    [disabled]="loading()">
                    {{ 'GENERAL.SHOW-HIDE-COLUMNS' | translate }}
                </button>
            }
        </div>
    </div>

    <mat-menu
        #filterMenu="matMenu"
        xPosition="before">
        <div (click)="$event.stopPropagation()">
            <div class="d-flex justify-content-between">
                <div class="menu-title">
                    {{ 'SHARED.TABLE-FILTERED-BY' | translate }}
                </div>
                <div
                    class="text-right pe-1 has-pointer"
                    (click)="closeFilterMenu()">
                    <mat-icon
                        aria-label="close"
                        class="icon-display"
                        fontSet="material-symbols-outlined"
                        >close</mat-icon
                    >
                </div>
            </div>

            <div class="tw-flex tw-flex-col">
                @for (item of keysForFilteringObj; track item) {
                    @if (
                        item.keyName !== 'status' &&
                        item.keyName !== 'deliveryNoteBundleStatus' &&
                        item.keyName !== 'discrepancies' &&
                        item.keyName !== 'palletTickets'
                    ) {
                        <mat-radio-button
                            aria-label="filter-button"
                            class="sorting-radio-button"
                            [value]="item.keyName"
                            (change)="onFilterChanges(item)"
                            [checked]="
                                selectedFilterKey()?.keyName === item.keyName &&
                                !isStatus
                            "
                            color="primary">
                            <div (click)="$event.stopPropagation()">
                                {{ item.label | translate }}
                            </div>
                        </mat-radio-button>
                    }
                    @if (item.keyName === 'discrepancies') {
                        <mat-radio-button
                            aria-label="sortByDiscrepancies"
                            class="sorting-radio-button"
                            color="primary"
                            [checked]="isDiscrepancies"
                            [matMenuTriggerFor]="filterByDiscrepancies">
                            <div (click)="$event.stopPropagation()">
                                {{ item.label | translate }}
                            </div>
                        </mat-radio-button>
                    }
                    @if (item.keyName === 'palletTickets') {
                        <mat-radio-button
                            aria-label="sortByPalletTicket"
                            class="sorting-radio-button"
                            color="primary"
                            [checked]="isPalletTickets"
                            [matMenuTriggerFor]="filterByPallets">
                            <div (click)="$event.stopPropagation()">
                                {{ item.label | translate }}
                            </div>
                        </mat-radio-button>
                    }
                }

                <mat-radio-button
                    aria-label="sort-by-status"
                    class="sorting-radio-button"
                    aria-labelledby="sorted-by"
                    color="primary"
                    [checked]="isStatus"
                    [matMenuTriggerFor]="filterByStatus">
                    <div (click)="$event.stopPropagation()">
                        {{
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE'
                                | translate
                        }}
                    </div>
                </mat-radio-button>

                <div class="action-bar">
                    <button
                        mat-button
                        (click)="resetFilter()">
                        <span>Reset</span>
                    </button>
                </div>
            </div>
        </div>
    </mat-menu>

    <mat-menu #filterByStatus="matMenu">
        <mat-radio-group
            class="sorting-radio-group"
            [(ngModel)]="selectedStatus">
            @for (status of statuses; track status) {
                <mat-radio-button
                    aria-label="status-button"
                    class="sorting-radio-button"
                    [value]="status.keyName"
                    (change)="onStatusFilterChanges(status)"
                    color="primary">
                    <div (click)="$event.stopPropagation()">
                        {{ status.label | translate }}
                    </div>
                </mat-radio-button>
            }
        </mat-radio-group>
    </mat-menu>

    <mat-menu #filterByDiscrepancies="matMenu">
        <div>
            <mat-radio-group
                class="sorting-radio-group"
                [(ngModel)]="selectedDiscrepancies">
                @for (bool of boolStatus; track bool) {
                    <mat-radio-button
                        aria-label="discrepancy-button"
                        [value]="bool.keyName"
                        (change)="onDiscrepanciesFilterChanges(bool)"
                        color="primary">
                        <div (click)="$event.stopPropagation()">
                            {{ bool.label | translate }}
                        </div>
                    </mat-radio-button>
                }
            </mat-radio-group>
        </div>
    </mat-menu>

    <mat-menu #filterByPallets="matMenu">
        <div>
            <mat-radio-group
                class="sorting-radio-group"
                [(ngModel)]="selectedPalletTickets">
                @for (bool of boolStatus; track bool) {
                    <mat-radio-button
                        aria-label="pallets-button"
                        class="sorting-radio-button"
                        [value]="bool.keyName"
                        (change)="onPalletFilterChanges(bool)"
                        color="primary">
                        <div (click)="$event.stopPropagation()">
                            {{ bool.label | translate }}
                        </div>
                    </mat-radio-button>
                }
            </mat-radio-group>
        </div>
    </mat-menu>
</div>
